/**
 * Utilitário para recortar imagens no cliente
 * Gera uma imagem quadrada a partir de uma área selecionada
 */

export interface Area {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Cria uma imagem a partir de uma URL
 */
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })

/**
 * Converte graus para radianos
 */
function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180
}

/**
 * Retorna as novas dimensões de um bounding box após rotação
 */
function rotateSize(width: number, height: number, rotation: number): { width: number; height: number } {
  const rotRad = getRadianAngle(rotation)
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  }
}

/**
 * Recorta uma imagem a partir de uma área selecionada
 * 
 * @param imageSrc - URL da imagem original (pode ser data URL)
 * @param pixelCrop - Área de recorte em pixels { x, y, width, height }
 * @param rotation - Rotação em graus (opcional)
 * @param outputSize - Tamanho do output em pixels (padrão 256)
 * @returns Promise com Blob da imagem recortada
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  outputSize = 256
): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Não foi possível obter contexto do canvas')
  }

  const rotRad = getRadianAngle(rotation)

  // Calcular bounding box da imagem rotacionada
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation)

  // Definir tamanho do canvas para conter a imagem rotacionada
  canvas.width = bBoxWidth
  canvas.height = bBoxHeight

  // Transladar para centro -> rotacionar -> transladar de volta
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
  ctx.rotate(rotRad)
  ctx.translate(-image.width / 2, -image.height / 2)

  // Desenhar imagem rotacionada
  ctx.drawImage(image, 0, 0)

  // Extrair área recortada
  const croppedCanvas = document.createElement('canvas')
  const croppedCtx = croppedCanvas.getContext('2d')

  if (!croppedCtx) {
    throw new Error('Não foi possível obter contexto do canvas de recorte')
  }

  // Definir tamanho de saída (quadrado)
  croppedCanvas.width = outputSize
  croppedCanvas.height = outputSize

  // Desenhar área recortada no canvas de saída, redimensionando para o tamanho desejado
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  )

  // Converter para Blob
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Falha ao criar blob da imagem'))
        }
      },
      'image/jpeg',
      0.9 // Qualidade JPEG (0.9 = 90%)
    )
  })
}

/**
 * Converte um Blob para File
 */
export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type, lastModified: Date.now() })
}
